import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import TreeView from "@mui/lab/TreeView";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";

import { setAdminOpenedNodes } from "../../actions";
import { useGetCompanyRoles } from "../../hooks";
import AdminCompanySelector from "./AdminCompanySelector";
import { sidebarmenuitems } from "./sidebarmenuitems";
import AdminSidebarTreeItem from "./Users/AdminSidebarTreeItem";

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    minWidth: 0,
    minHeight: "100%",
    background: "#f7f8fa",
    borderRight: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
    marginLeft: -24,
    marginTop: -24,
    padding: "9px 7px 14px 6px",

    "& .MuiTreeItem-iconContainer": {
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
      marginRight: 0,
      width: 14,
      minWidth: 14,
      height: 20,
      color: alpha(theme.palette.text.primary, 0.58),

      "& .MuiSvgIcon-root": {
        fontSize: 17,
      },
    },

    "& .MuiTreeItem-content": {
      paddingLeft: "2px !important",
      paddingRight: "0 !important",
    },

    "& .MuiTreeItem-label": {
      minWidth: 0,
      paddingLeft: "0 !important",
    },

    "& .MuiTreeItem-group": {
      marginLeft: "4px !important",
      paddingLeft: 6,
      borderLeft: `1px solid ${alpha(theme.palette.text.primary, 0.055)}`,
    },

    "& .MuiTreeItem-content.Mui-selected": {
      backgroundColor: `${alpha(theme.palette.primary.main, 0.06)} !important`,
    },

    "& .MuiTreeItem-content.Mui-selected:hover": {
      backgroundColor: `${alpha(theme.palette.primary.main, 0.09)} !important`,
    },

    "& .MuiTreeItem-content:focus": {
      backgroundColor: "transparent",
    },
  },

  selectorArea: {
    marginBottom: 5,
    padding: "3px 2px 6px",
    borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.055)}`,

    // Keep the floating label readable and prevent the outlined border
    // from visually running through the label text.
    "& .MuiInputLabel-root": {
      top: 1,
      zIndex: 1,
    },
    "& .MuiInputLabel-root.MuiInputLabel-shrink": {
      padding: "0 4px",
      backgroundColor: "#f7f8fa",
      marginLeft: -2,
    },

    // Slightly calmer selector proportions.
    "& .MuiOutlinedInput-root": {
      minHeight: 46,
    },
  },

  treeArea: {
    paddingTop: 1,
  },
}));

const AdminSidebar = ({ attentionStatus }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { companyId, usersRights } = useSelector(
    state => state.api.companyAdmin.company
  );

  //TODO: Change to real backend data once the backend is ready to provide it.
  /* const effectiveAttentionStatus =
    attentionStatus ?? reduxAttentionStatus ?? {}; */

  //TODO: Remove this hardcoded attention status once the backend is ready to provide it
  const effectiveAttentionStatus = {
    organizationDetails: {
      requiresAction: true,
      severity: "warning",
      messageId: "admin.sidebar.attention.organizationDetails",
    },
    billingInformation: {
      requiresAction: true,
      severity: "warning",
      messageId: "admin.sidebar.attention.billingInformation",
    },
    paymentMethod: {
      requiresAction: true,
      severity: "warning",
      messageId: "admin.sidebar.attention.paymentMethod",
    },
  };

  const openedNodes = useSelector(state => state.api.companyAdmin.openedNodes);

  const { execute: getCompanyRoles } = useGetCompanyRoles();

  useEffect(() => {
    if (companyId !== null) {
      getCompanyRoles();
    }
  }, [companyId, getCompanyRoles]);

  useEffect(() => {
    const prevNodeIds = JSON.parse(localStorage.getItem("companyOpenedNodes"));
    if (Array.isArray(prevNodeIds) && prevNodeIds.length > 0) {
      dispatch(setAdminOpenedNodes({ items: prevNodeIds }));
    }
  }, [dispatch]);

  const handleOnNodeToggle = (e, nodeIds) => {
    e.preventDefault();
    localStorage.setItem("companyOpenedNodes", JSON.stringify(nodeIds));
    dispatch(setAdminOpenedNodes({ items: nodeIds }));
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.selectorArea}>
        <AdminCompanySelector />
      </Box>

      <Box className={classes.treeArea}>
        <TreeView
          multiSelect
          defaultCollapseIcon={<ExpandMoreRoundedIcon />}
          defaultExpandIcon={<ChevronRightRoundedIcon />}
          expanded={openedNodes}
          onNodeToggle={handleOnNodeToggle}
        >
          {sidebarmenuitems.map((item, idx) => (
            <AdminSidebarTreeItem
              key={`tree-item-${idx}`}
              idx={idx}
              link={item.link}
              icon={item.icon}
              label={item.label}
              enabledByRights={item.enabledByRights}
              usersRights={usersRights}
              childtree={item.childtree}
              attentionStatus={effectiveAttentionStatus}
            />
          ))}
        </TreeView>
      </Box>
    </Box>
  );
};

AdminSidebar.defaultProps = {
  attentionStatus: undefined,
};

AdminSidebar.propTypes = {
  attentionStatus: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({
        requiresAction: PropTypes.bool,
        severity: PropTypes.oneOf(["warning", "error"]),
        message: PropTypes.string,
        messageId: PropTypes.string,
      }),
    ])
  ),
};

export default AdminSidebar;
